import React, { useEffect, useMemo } from 'react'
import { getCurrentLocale, formatCurrency } from 'utils'
import { formatSchedulePattern } from 'utils/scheduleUtils'
import { trackEvent } from 'utils/analytics'

function formatDateRange(sessions) {
  if (!sessions || sessions.length === 0) return null
  const locale = getCurrentLocale() || 'zh-TW'
  const fmt = (isoString) =>
    new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(isoString))
  const first = sessions[0].start_time
  const last = sessions[sessions.length - 1].start_time
  if (sessions.length === 1) return fmt(first)
  return `${fmt(first)} ~ ${fmt(last)}`
}

export default function GroupClassEnrollModal({ groupClass, onClose, onConfirm, translations = {} }) {
  const t = {
    enrollTitle: translations.enrollTitle || '確認報名',
    seriesInfo: translations.seriesInfo || '課程資訊',
    confirmEnroll: translations.confirmEnroll || '確認並付款',
    cancel: translations.cancel || '取消',
    totalSessions: translations.totalSessions || '共 %{count} 堂',
  }

  const scheduleLabel = useMemo(() => formatSchedulePattern(groupClass?.schedule_rule), [groupClass?.schedule_rule])
  const dateRangeLabel = useMemo(() => formatDateRange(groupClass?.sessions), [groupClass?.sessions])

  const totalSessionsLabel = groupClass?.total_sessions != null
    ? t.totalSessions.replace('%{count}', groupClass.total_sessions)
    : null

  useEffect(() => {
    trackEvent('group_class_enroll_modal_open', { class_title: groupClass?.title })
    const handleEsc = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleEsc)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between shrink-0"
          style={{ backgroundColor: '#E6F0FF' }}
        >
          <span className="font-semibold" style={{ color: '#3366FF' }}>
            {t.enrollTitle}
          </span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="rounded-lg p-4 space-y-2" style={{ backgroundColor: '#E6F0FF' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#3366FF' }}>
              {t.seriesInfo}
            </p>
            <p className="font-semibold text-gray-900 text-sm">
              {groupClass.title}
            </p>
            {scheduleLabel && (
              <p className="text-sm text-gray-600">{scheduleLabel}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-600">
              {totalSessionsLabel && <span>{totalSessionsLabel}</span>}
              {dateRangeLabel && <span>{dateRangeLabel}</span>}
            </div>
            {groupClass.price != null && (
              <p className="text-sm font-bold" style={{ color: '#3366FF' }}>
                {formatCurrency(groupClass.price, groupClass.currency || 'TWD')}
              </p>
            )}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="px-6 pb-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
          >
            {t.cancel}
          </button>
          <button
            type="button"
            onClick={() => {
              trackEvent('group_class_enroll_confirm', { class_title: groupClass?.title, price: groupClass?.price })
              onConfirm()
            }}
            className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors cursor-pointer"
            style={{ backgroundColor: '#3366FF' }}
          >
            {t.confirmEnroll}
          </button>
        </div>
      </div>
    </div>
  )
}
